import { Typography } from "@mui/material";

interface UserIdentifierDisplayProps {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  role: string | null | undefined;
  grade: number | null | undefined;
}

/**
 * Displays a user's full name with a secondary identifier to disambiguate
 * users who share the same name.
 *
 * - Non-students: shows their role
 * - Students with a grade: shows "Grade {n}"
 * - Students without a grade: shows "No Grade Level"
 */
export const UserIdentifierDisplay = ({
  firstName,
  lastName,
  role,
  grade,
}: UserIdentifierDisplayProps) => {
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "Unknown User";
  const isStudent = role?.toLowerCase() === "student";

  const ACRONYMS = new Set(["it", "hr", "id"]);

  const formatRole = (r: string) =>
    r.split("_").map((w) =>
      ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    ).join(" ");

  let secondary: string | null;
  if (!role) {
    secondary = null;
  } else if (!isStudent) {
    secondary = formatRole(role);
  } else if (grade != null) {
    secondary = `Grade ${grade}`;
  } else {
    secondary = "No Grade Level";
  }

  return (
    <span style={{ display: "flex", flexDirection: "column" }}>
      <span>{fullName}</span>
      {secondary != null && (
        <Typography component="span" variant="caption" color="text.secondary">
          ({secondary})
        </Typography>
      )}
    </span>
  );
};
